import EVENTS from "core/customEvent/events.js";
import anyHandlesOutsideImage from "./anyHandlesOutsideImage.js";
import { removeToolState } from "component/toolManager/toolState.js";
import math from "core/math";
import { state } from "component/molecules/viewport/toolStates/store.js";

import triggerEvent from "core/customEvent/triggerEvent.js";

import { getEnabledElement } from "component/toolManager/enabledElements.js";

const _moveEvents = {
  mouse: [EVENTS.MOUSE_MOVE, EVENTS.MOUSE_DRAG],
  touch: [EVENTS.TOUCH_DRAG]
};

const _moveEndEvents = {
  mouse: [EVENTS.MOUSE_UP, EVENTS.MOUSE_CLICK],
  touch: [EVENTS.TOUCH_END, EVENTS.TOUCH_PINCH, EVENTS.TAP]
};

/**
 * Move a new handle
 * @public
 * @method moveNewHandle
 * @memberof Manipulators
 *
 * @param {*} evtDetail
 * @param {*} toolName
 * @param {*} annotation
 * @param {*} handle
 * @param {*} [options={}]
 * @param {Boolean}  [options.deleteIfHandleOutsideImage]
 * @param {function} [options.doneMovingCallback]
 * @param {Boolean}  [options.preventHandleOutsideImage]
 * @param {*} [interactionType=mouse]
 * @returns {undefined}
 */
export default function(
  evtDetail,
  toolName,
  annotation,
  handle,
  options,
  interactionType = "mouse"
) {
  // Use global defaults, unless overidden by provided options
  options = Object.assign(
    {
      deleteIfHandleOutsideImage: state.deleteIfHandleOutsideImage,
      preventHandleOutsideImage: state.preventHandleOutsideImage
    },
    options
  );

  //const element = evtDetail.element;
  const element = evtDetail.element.element;

  annotation.active = true;
  handle.active = true;
  state.isToolLocked = true;

  const moveHandler = _moveHandler.bind(
    this,
    toolName,
    annotation,
    handle,
    options,
    interactionType
  );
  // So we don't need to inline the entire `moveEndEventHandler` function
  const moveEndHandler = evt => {
    _moveEndHandler(
      toolName,
      annotation,
      handle,
      options,
      interactionType,
      {
        moveHandler,
        moveEndHandler
      },
      evt
    );
  };

  // Add event listeners
  _moveEvents[interactionType].forEach(eventType => {
    element.addEventListener(eventType, moveHandler);
  });
  _moveEndEvents[interactionType].forEach(eventType => {
    element.addEventListener(eventType, moveEndHandler);
  });
  element.addEventListener(EVENTS.TOUCH_START, _stopImmediatePropagation);
}

function _moveHandler(
  toolName,
  annotation,
  handle,
  options,
  interactionType,
  evt
) {
  const { currentPoints, image } = evt.detail;
  let element = evt.detail.element.element;
  const page = currentPoints.page;
  const fingerOffset = -57;
  const targetLocation = math.coordinate.pageToPixel(
    element,
    interactionType === "touch" ? page.x + fingerOffset : page.x,
    interactionType === "touch" ? page.y + fingerOffset : page.y
  );

  annotation.invalidated = true;
  handle.active = true;
  handle.x = targetLocation.x;
  handle.y = targetLocation.y;

  if (annotation.willbeRemove) {
    annotation.moveCount++;
  }
  if (annotation.moveCount > 5) {
    annotation.willbeRemove = false;
  }

  //annotation.willbeRemove = false;

  // End point 가 움직이고 있다고 가정. 영향 받는 주변 점의 위치를 수정
  if (annotation.handles.lb && annotation.handles.rt) {
    annotation.handles.lb.y = targetLocation.y;
    annotation.handles.rt.x = targetLocation.x;
  }
  /// middle point가 저장되는 도구 옵션이 있는경우,
  /// 이동중에 감지된 점을 모두 저장
  if (annotation.storeMiddlePoint && annotation.storeMiddlePoint === true) {
    annotation.middlePoint.push({
      x: targetLocation.x,
      y: targetLocation.y
    });
  }

  if (options && options.preventHandleOutsideImage) {
    math.range.clipToBox(handle, image);
  }
  // viewport 영상 업데이트
  let enabledElement = getEnabledElement(element);
  enabledElement.needsRedraw = true;

  const eventType = EVENTS.MEASUREMENT_MODIFIED;
  const modifiedEventData = {
    toolName,
    element,
    measurementData: annotation
  };

  triggerEvent(element, eventType, modifiedEventData);
}

function _moveEndHandler(
  toolName,
  annotation,
  handle,
  options,
  interactionType,
  { moveHandler, moveEndHandler },
  evt
) {
  //const { element, currentPoints } = evt.detail;
  const { currentPoints } = evt.detail;
  let element = evt.detail.element.element;
  const page = currentPoints.page;
  const fingerOffset = -57;

  const targetLocation = math.coordinate.pageToPixel(
    element,
    interactionType === "touch" ? page.x + fingerOffset : page.x,
    interactionType === "touch" ? page.y + fingerOffset : page.y
  );

  // "Release" the handle
  annotation.active = false;
  annotation.invalidated = true;
  handle.active = false;
  handle.x = targetLocation.x;
  handle.y = targetLocation.y;
  state.isToolLocked = false;

  // End point 가 움직이고 있다고 가정. 영향 받는 주변 점의 위치를 수정
  if (annotation.handles.lb && annotation.handles.rt) {
    annotation.handles.lb.y = targetLocation.y;
    annotation.handles.rt.x = targetLocation.x;
  }

  // Remove event listeners
  _moveEvents[interactionType].forEach(eventType => {
    element.removeEventListener(eventType, moveHandler);
  });
  _moveEndEvents[interactionType].forEach(eventType => {
    element.removeEventListener(eventType, moveEndHandler);
  });
  element.removeEventListener(EVENTS.TOUCH_START, _stopImmediatePropagation);

  // TODO: WHY?
  // Why would a Touch_Pinch or Touch_Press be associated with a new handle?
  if (evt.type === EVENTS.TOUCH_PINCH || evt.type === EVENTS.TOUCH_PRESS) {
    handle.active = false;

    // 영상 업데이트

    if (typeof options.doneMovingCallback === "function") {
      options.doneMovingCallback();
    }

    return;
  }

  if (options.preventHandleOutsideImage) {
    math.range.clipToBox(handle, evt.detail.image);
  }

  // If any handle is outside the image, delete the tool data
  if (
    (options.deleteIfHandleOutsideImage &&
      anyHandlesOutsideImage(evt.detail, annotation.handles)) ||
    annotation.willbeRemove
  ) {
    removeToolState(element, toolName, annotation);
  }

  if (typeof options.doneMovingCallback === "function") {
    options.doneMovingCallback();
  }

  // Update Image
  // viewport 영상 업데이트
  let enabledElement = getEnabledElement(element);
  enabledElement.needsRedraw = true;
}

/*
 * MoveNewHandle ends
 *
 * @private
 * @function _stopImmediatePropagation
 *
 * @param {*} evt
 * @returns {Boolean} false
 */
function _stopImmediatePropagation(evt) {
  evt.stopImmediatePropagation();

  return false;
}
